import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Language {
    id: number;
    name: string;
}

export interface Localization {
    language: Language;
    name?: string;
}

export interface PaymentTerm {
    id: number;
    code: string;
    name: string;
    localizations: Localization[];
}

export async function getPaymentTerm(id: number): Promise<PaymentTerm> {
    const result = await rms2Fetch<PaymentTerm>(`paymentterms/${id}`);
    if (!result) throw new Error('Empty response for getPaymentTerm');
    return result;
}

export async function createPaymentTerm(pt: Partial<PaymentTerm>): Promise<PaymentTerm> {
    const result = await rms2Fetch<PaymentTerm>('paymentterms', {
        method: 'POST',
        body: JSON.stringify({
            code: pt.code,
            name: pt.name,
            localizations: pt.localizations?.map((l) => ({ languageId: l.language.id, name: l.name }))
        })
    });
    if (!result) throw new Error('Empty response for createPaymentTerm');
    return result;
}

export async function updatePaymentTerm(pt: PaymentTerm): Promise<PaymentTerm> {
    const result = await rms2Fetch<PaymentTerm>(`paymentterms/${pt.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            code: pt.code,
            name: pt.name,
            localizations: pt.localizations?.map((l) => ({ languageId: l.language.id, name: l.name }))
        })
    });
    if (!result) throw new Error('Empty response for updatePaymentTerm');
    return result;
}

export async function deletePaymentTerm(id: number): Promise<void> {
    await rms2Fetch<void>(`paymentterms/${id}`, { method: 'DELETE' });
}

export async function getLanguages(): Promise<Language[]> {
    const response = await rms2Fetch<{ languages: Language[] }>('languages');
    return response?.languages ?? [];
}
